import { trackPrivateHealthQuoteTableModalOpened } from '@getpopsure/analytics';
import { calendly } from '@getpopsure/private-constants';
import {
  EmploymentStatus,
  getPriceBreakdown,
} from '@getpopsure/private-health-insurance-pricing-engine';
import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';
import { ComparisonTable } from '@popsure/dirty-swan';
import { SectionHeader } from 'components/sectionHeader';
import { useCallback, useState } from 'react';
import AnimateHeight from 'react-animate-height';

import {
  ModalWithTracking,
  TableData,
  Tariff,
  TariffsWithPublic,
} from '../models';
import { getPublicHealthContribution } from '../util';
import {
  CashbackModal,
  ContributionModal,
  TableModal,
} from './components/modals';
import styles from './Coverage.module.scss';
import { getTableData, getTableHeaders } from './tableData';
import {
  getTableData as getPreviewTableData,
  getTableHeaders as getPreviewTableHeaders,
} from './tableData/preview';

type CoverageProps = {
  isAboveIncomeThreshold: boolean;
  isPrivateLight?: boolean;
  birthYear?: number;
  income?: number;
  employmentStatus?: EmploymentStatus;
  ctaAction: () => void;
  showHeaderPrice?: boolean;
  isFamily?: boolean;
};

const useCoverage = ({
  isAboveIncomeThreshold,
  isPrivateLight,
  birthYear,
  income,
  employmentStatus,
  showHeaderPrice = true,
  isFamily,
}: CoverageProps) => {
  const [modal, setModal] = useState<null | ModalWithTracking>();

  const handleSetModal = useCallback((newModal: null | ModalWithTracking) => {
    setModal(newModal);

    if (newModal) {
      trackPrivateHealthQuoteTableModalOpened({
        tariff_name: newModal?.tariff,
        field_key: newModal?.fieldKey,
        is_landing_page: true,
      });
    }
  }, []);

  const calculatorIsFilledAndAboveTreshold =
    isAboveIncomeThreshold &&
    birthYear !== undefined &&
    income !== undefined &&
    employmentStatus !== undefined &&
    isPrivateLight !== undefined;

  let publicHealthContribution:
    | ReturnType<typeof getPublicHealthContribution>
    | undefined;

  let publicHealthPriceString: string | undefined;

  let shortTermPrice: number | undefined;
  let standardPrice: number | undefined;
  let plusPrice: number | undefined;
  let premiumPrice: number | undefined;

  if (calculatorIsFilledAndAboveTreshold) {
    publicHealthContribution = getPublicHealthContribution({
      birthYear,
      income,
      employmentStatus,
    });

    if (publicHealthContribution) {
      const priceStringPrefix = employmentStatus === 'OTHER' ? '~' : '';
      publicHealthPriceString = `${priceStringPrefix}${englishFormattedEuroCurrency(
        Math.round(publicHealthContribution.total),
        true
      )}`;
    }

    shortTermPrice = getPriceBreakdown({
      tariff: 'HiMedicalPlus',
      birthYear,
      income,
      employmentStatus,
    }).contributions.own;

    standardPrice = getPriceBreakdown({
      tariff: 'NKSelectS',
      birthYear,
      income,
      employmentStatus,
    }).contributions.own;

    plusPrice = getPriceBreakdown({
      tariff: 'NKSelectL',
      birthYear,
      income,
      employmentStatus,
    }).contributions.own;

    premiumPrice = getPriceBreakdown({
      tariff: 'NKSelectXL',
      birthYear,
      income,
      employmentStatus,
    }).contributions.own;
  }

  const tableData: Array<TableData> = getTableData({
    eligibleForShortTerm: isPrivateLight,
    publicHealthPriceString,
    birthYear,
    income,
    isAboveIncomeThreshold,
    shortTermPrice,
    standardPrice,
    plusPrice,
    premiumPrice,
    isFamily,
  });

  let handleOpenContributionModal = (tariff: TariffsWithPublic) => {};
  let handleCashbackModal = (tariff: Tariff) => {};

  if (calculatorIsFilledAndAboveTreshold) {
    handleOpenContributionModal = (tariff: TariffsWithPublic) => {
      handleSetModal({
        tariff,
        fieldKey: 'contribution',
        title: 'How is my price calculated?',
        children: (
          <ContributionModal
            priceData={
              tariff === 'Public'
                ? publicHealthContribution
                : getPriceBreakdown({
                    birthYear,
                    employmentStatus,
                    income,
                    tariff,
                  })
            }
          />
        ),
      });
    };
  }

  handleCashbackModal = (tariff: Tariff) =>
    handleSetModal({
      tariff,
      fieldKey: 'cashback',
      title: 'Cashback',
      children: (
        <CashbackModal
          birthYear={birthYear ?? 0}
          income={income ?? 0}
          tariff={tariff}
        />
      ),
    });

  const tableHeaders = getTableHeaders({
    handleSetModal,
    handleOpenContributionModal,
    showHeaderPrice,
    handleCashbackModal,
    isFamily,
    employmentStatus,
  });

  return {
    data: tableData,
    headers: tableHeaders,
    modal,
    setModal: handleSetModal,
  };
};

export const Coverage = (coverageProps: CoverageProps) => {
  const {
    isAboveIncomeThreshold,
    birthYear,
    income,
    employmentStatus,
    ctaAction,
    isFamily,
  } = coverageProps;
  const { headers, data, modal, setModal } = useCoverage(coverageProps);

  const mainCalculatorIsFilled =
    birthYear !== undefined &&
    income !== undefined &&
    employmentStatus !== undefined;

  const showPreviewCoverage =
    (!isFamily && !mainCalculatorIsFilled) ||
    (!isFamily && !isAboveIncomeThreshold);

  const mainNotEligible = mainCalculatorIsFilled && !isAboveIncomeThreshold;

  return (
    <>
      <section className={`p-container ${styles.container}`}>
        <div className={styles.titleContainer}>
          <SectionHeader
            title="Our plans"
            titleClass="p-h1 p--serif"
            centeredOnMobile
          />
        </div>
        <AnimateHeight
          className={styles.tableWrapper}
          duration={300}
          height={showPreviewCoverage ? 548 : 'auto'}
        >
          <div
            className={`d-flex fd-column ai-center jc-end ${
              styles.tableOverlay
            } ${showPreviewCoverage ? styles.fadeIn : styles.fadeOut}`}
          />
          <div
            className={`d-flex fd-column ai-center gap16 ${styles.ctaContainer}`}
          >
            {mainNotEligible && (
              <p className="p-p tc-grey-500">
                <a
                  className="p-a fw-bold"
                  href={calendly.healthInsuranceAdvice}
                  target="_blank"
                  rel="noreferrer"
                >
                  Contact us
                </a>{' '}
                to find out which options are available to you.
              </p>
            )}
            {!mainCalculatorIsFilled && !isFamily && (
              <>
                <p className="p-p tc-grey-500">
                  Use our calculator to see what’s available for you.
                </p>
                <button
                  type="button"
                  className="p-btn--primary"
                  onClick={ctaAction}
                >
                  Show full coverage
                </button>
              </>
            )}
          </div>
          {!showPreviewCoverage ? (
            <ComparisonTable
              headers={headers}
              data={data}
              cellWidth={200}
              stickyHeaderTopOffset={64}
              growContent
              collapsibleSections
            />
          ) : (
            <ComparisonTable
              headers={getPreviewTableHeaders()}
              data={getPreviewTableData() as TableData[]}
              cellWidth={200}
              growContent
            />
          )}
        </AnimateHeight>
      </section>

      {modal && <TableModal modal={modal} setModal={setModal} />}
    </>
  );
};
